import React, { useMemo, useState, useRef, useEffect } from 'react';
import { resolveTabs, TAB_REGISTRY } from '../../../utils/tabRegistry';
import styles from './detailsTab.module.css';
import { ReactComponent as Arrow } from '../../../assets/icons/trvto_arrowRightWhite.svg';
import VoiceSummary from '../../voiceSummary/VoiceSummary';
import { sectionVoiceScript, SECTION_LABELS } from '../../voiceSummary/tripVoiceScript';

const DetailsTab = ({
    context,
    data,
    dates,
    deleteUnavailableDate,
    setDateToDelete,
    deleteModal,
    setDeleteModal,
    whichSection,
    refresh,
    type,
    loading
}) => {
    const tabsRef = useRef(null);
    const [activeTab, setActiveTab] = useState(type ? 'overview' : 'itinerary');
    const [showArrow, setShowArrow] = useState(false);

    const tabs = resolveTabs(TAB_REGISTRY, { ...context, data });

    // fall back to the first tab when the default one isn't offered (e.g. no Itinerary for activities / guides)
    const activeTabConfig = tabs.find((tabItem) => tabItem.id === activeTab) || tabs[0];

    // users on a trip page can listen to the open tab (itinerary, inclusions, carry, terms)
    const [voiceGroup, setVoiceGroup] = useState(-1);
    const voice = useMemo(
        () =>
            context?.role === 'user' && context?.flow === 'trip' && SECTION_LABELS[activeTabConfig?.id]
                ? sectionVoiceScript(activeTabConfig.id, data)
                : null,
        [context?.role, context?.flow, activeTabConfig?.id, data]
    );

    // 👉 scroll function
    const scrollTabs = () => {
        tabsRef.current.scrollBy({
            left: 200,
            behavior: 'smooth'
        });
    };

    // 👉 check if scroll is needed
    useEffect(() => {
        const el = tabsRef.current;
        if (!el) return;

        const checkScroll = () => {
            const isScrollable = el.scrollWidth > el.clientWidth;
            const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 5;

            setShowArrow(isScrollable && !isAtEnd);
        };

        checkScroll();

        el.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);

        return () => {
            el.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [tabs]);

    return (
        <div>
            <div className={styles.tabsContainer}>
                <div className={styles.detailsTabs} ref={tabsRef}>
                    {tabs.map((tabItem) => {
                        const isTabActive = tabItem.id === activeTabConfig?.id;

                        return (
                            <button
                                key={tabItem.id}
                                className={`${styles.detailsTabButton} ${isTabActive ? styles.detailsTabButtonHi : ''}`}
                                onClick={() => {
                                    setActiveTab(tabItem.id);
                                    setVoiceGroup(-1);
                                }}
                            >
                                {tabItem.label}
                            </button>
                        );
                    })}
                </div>

                {/* 👉 Arrow */}
                {showArrow && (
                    <div onClick={scrollTabs} className={styles.arrowBtn}>
                        <Arrow />
                    </div>
                )}
            </div>

            <div className={styles.detailsInfoDiv}>
                {voice?.lines?.length > 0 && (
                    <VoiceSummary
                        key={activeTabConfig.id}
                        lines={voice.lines}
                        label='Listen'
                        onIndexChange={(i) => setVoiceGroup(i < 0 ? -1 : (voice.groups[i] ?? -1))}
                    />
                )}
                {activeTabConfig?.render({
                    activeDay: activeTabConfig?.id === 'itinerary' ? voiceGroup : -1,
                    data,
                    flow: context?.flow,
                    dates,
                    whichSection,
                    refresh,
                    deleteUnavailableDate,
                    setDateToDelete,
                    deleteModal,
                    setDeleteModal,
                    loading
                })}
            </div>
        </div>
    );
};

export default DetailsTab;
